import { useMemo, useState, useEffect } from 'react';
import { SandpackProvider, SandpackLayout, SandpackPreview, useSandpack } from '@codesandbox/sandpack-react';
import { Loading } from '@appkit4/react-components';
import { injectFontFix, SANDPACK_EXTERNAL_RESOURCES } from '@utils/sandpackAssets';
import { DesignSystems } from '@utils/shared';

import { SANDBOX_BUNDLER_URL } from '@config/sandbox';
import '@components/Sandpack/SandpackRenderer.scss';

interface PublicSandpackRendererProps {
    files: Record<string, any>;
    dependencies: Record<string, string>;
    templateVersion?: string;
    designSystem?: DesignSystems;
}

// Inner component to access Sandpack context for loading state
const SandpackContent = ({ onLoadingChange }: { onLoadingChange: (loading: boolean) => void }) => {
    const { listen } = useSandpack();

    useEffect(() => {
        const stopListening = listen((msg: any) => {
            if (msg.type === 'start') {
                onLoadingChange(true);
            }
            if (msg.type === 'done' || msg.type === 'success') {
                onLoadingChange(false);
            }
        });

        return () => {
            stopListening();
        };
    }, [listen, onLoadingChange]);

    return null;
};

const PublicSandpackRenderer = ({
    files,
    dependencies,
    templateVersion,
    designSystem,
}: PublicSandpackRendererProps) => {
    const [isLoading, setIsLoading] = useState(true);

    const customSetup = useMemo(
        () => ({
            dependencies,
            npmRegistries: [
                {
                    enabledScopes: ['@appkit4', 'ckeditor5-appkit-build'],
                    limitToScopes: true,
                    registryUrl: `${window.location.protocol}//${window.location.host}/npm-proxy/`,
                    proxyEnabled: false,
                },
            ],
        }),
        [dependencies]
    );

    const customFiles = useMemo(() => {
        let result = files;
        if (templateVersion === 'v2') {
            const remapped: Record<string, any> = {};
            for (const [key, value] of Object.entries(files)) {
                const normalizedKey = key.replace(/^\//, '');
                if (normalizedKey === 'main.tsx') {
                    remapped['/index.tsx'] = value;
                } else {
                    remapped[key] = value;
                }
            }
            result = remapped;
        }
        return designSystem === 'Appkit' || designSystem === 'AppkitRebrand' || designSystem === 'NewEra'
            ? injectFontFix(result, designSystem)
            : result;
    }, [JSON.stringify(files), templateVersion, designSystem]); // eslint-disable-line react-hooks/exhaustive-deps
    // Note: files is intentionally omitted; JSON.stringify is used for complex object memoization

    return (
        <div className="public-sandbox-container">
            <SandpackProvider
                template={templateVersion === 'v2' ? 'react-ts' : 'react'}
                files={customFiles}
                customSetup={customSetup}
                options={{
                    externalResources:
                        designSystem === 'Appkit' || designSystem === 'AppkitRebrand' || designSystem === 'NewEra'
                            ? SANDPACK_EXTERNAL_RESOURCES
                            : [],
                    bundlerTimeOut: 300_000,
                    classes: {
                        'sp-wrapper': 'public-wrapper',
                    },
                    recompileDelay: 1000,
                    bundlerURL: SANDBOX_BUNDLER_URL,
                }}>
                <SandpackContent onLoadingChange={setIsLoading} />

                <SandpackLayout style={{ height: '100%' }}>
                    <SandpackPreview
                        showRefreshButton={false}
                        showOpenInCodeSandbox={false}
                        showNavigator={true}
                        showSandpackErrorOverlay={false}
                    />
                </SandpackLayout>

                {isLoading && (
                    <div className="public-sandbox-loading">
                        <Loading indeterminate />
                    </div>
                )}
            </SandpackProvider>
        </div>
    );
};

export default PublicSandpackRenderer;
